import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useAppSelector} from 'src/app/hooks';
import {CHARGE_POINT_STATUS_TYPE, CHARGING_STATUS_TYPE, CONNECTOR_STATUS_TYPE} from 'src/types';

/**
 * Refreshing a page from what the websocket already knows.
 *
 * `App` puts every message into `common`, so the browser learns about a charger going offline or a
 * session ending the moment it happens - but nothing told the pages that *count* those things to ask
 * again, and their numbers stood still until a reload. These hooks close that gap: they watch the
 * store for news and call back once it settles, leaving what to re-request to the caller.
 *
 * The timing is the delicate part, so it lives in one place:
 * - the **first** value is skipped, because whoever mounted the page has just asked the server;
 * - a refresh is **scheduled and then left alone** rather than reset per message - resetting is what
 *   a plain debounce does, and a steady trickle of messages postpones it for ever;
 * - there is a **floor** between two refreshes, so a busy site cannot turn this into polling.
 */
const DEFAULT_DELAY = 2000;
const DEFAULT_MIN_GAP = 5000;

export interface LiveRefreshOptions {
    /** How long to let a burst settle. One transition arrives as two or three messages. */
    delay?: number;
    /** The floor between two refreshes. */
    minGap?: number;
}

const useThrottledRefresh = (onChange: () => void, options?: LiveRefreshOptions) => {
    const delay = options?.delay ?? DEFAULT_DELAY;
    const minGap = options?.minGap ?? DEFAULT_MIN_GAP;

    const callback = useRef(onChange);
    const scheduled = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastRun = useRef(0);
    const seenFirst = useRef(false);

    useEffect(() => {
        callback.current = onChange;
    }, [onChange]);

    useEffect(
        () => () => {
            scheduled.current && clearTimeout(scheduled.current);
        },
        []
    );

    return useCallback(
        () => {
            if (!seenFirst.current) {
                seenFirst.current = true;
                return;
            }
            if (scheduled.current) {
                return;
            }

            const sinceLastRun = Date.now() - lastRun.current;
            scheduled.current = setTimeout(() => {
                scheduled.current = null;
                lastRun.current = Date.now();
                callback.current();
            }, Math.max(delay, minGap - sinceLastRun));
        },
        [delay, minGap]
    );
};

/**
 * News about chargers and their connectors: a station going offline, a connector changing state.
 *
 * `CHARGING_STATUS` is deliberately not watched here. It carries the live power of a running session
 * and arrives with every meter value, so one charging car would keep asking for counts that cannot
 * have changed. Any message on the two channels below counts as news, even one repeating a status the
 * store already held: the row is replaced rather than compared, so the array is new either way, and
 * a repeat still means the server had something to say.
 */
export const useLiveChargerRefresh = (onChange: () => void, options?: LiveRefreshOptions) => {
    const request = useThrottledRefresh(onChange, options);
    const chargePoints: CHARGE_POINT_STATUS_TYPE[] = useAppSelector(
        (state) => state.common.CHARGE_POINT_STATUS
    );
    const connectors: CONNECTOR_STATUS_TYPE[] = useAppSelector((state) => state.common.CONNECTOR_STATUS);

    useEffect(() => {
        request();
    }, [chargePoints, connectors, request]);
};

/**
 * News about charging sessions: one started, one ended.
 *
 * A session always moves its connector - Preparing to Charging, Charging to Finishing - so connector
 * messages carry it. `CHARGING_STATUS` is read for its status alone, as a fingerprint: the watts and
 * the amperage in it change every few seconds and say nothing about how many sessions are running.
 */
export const useLiveSessionRefresh = (onChange: () => void, options?: LiveRefreshOptions) => {
    const request = useThrottledRefresh(onChange, options);
    const connectors: CONNECTOR_STATUS_TYPE[] = useAppSelector((state) => state.common.CONNECTOR_STATUS);
    const chargingStatuses: CHARGING_STATUS_TYPE[] = useAppSelector(
        (state) => state.common.CHARGING_STATUS
    );

    // Per connector, not per charger: two sockets of one station run two sessions, and keyed on the
    // station alone the second one starting or ending left the fingerprint unchanged.
    const chargingFingerprint = useMemo(
        () => chargingStatuses
            .map((item) => `${item.chargerId}:${item.connectorId}:${item.status}`)
            .sort()
            .join('|'),
        [chargingStatuses]
    );

    useEffect(() => {
        request();
    }, [connectors, chargingFingerprint, request]);
};
